import { LayoutProvider } from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import { Redirect, Route, BrowserRouter as Router, Switch } from "react-router-dom";

import style from "./blancco-common-cloud.scss";
import { MANAGEMENT_CONSOLE_LICENSE } from "./licenses/bundles";
import { AIPanel } from "components/ai-panel/AIPanel";
import { AIPanelProvider } from "components/ai-panel/AIPanelContext";
import { AppLifecycle, resolveAppLifecycle } from "components/appLifecycle";
import { DevTools } from "components/dev-tools/DevTools";
import i18n from "components/i18n";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import SurveyMonkeyWidget from "components/products/SurveyMonkeyWidget";
import {
    ALL_BMS_REPORTS_ROUTE,
    ALL_WORKFLOWS_ROUTE,
    API_KEYS_ROUTE,
    DELIVERYHISTORY_LICENSES_ROUTE,
    enableBmsReportChildRoutes,
    enableCustomAssetViewRoutes,
    enableCustomReportViewRoutes,
    enableLicenseChildRoutes,
    enableLicenseExtractionChildRoutes,
    enableOrDisableAllApiKeys,
    enableSerialLicenseExtractionChildRoutes,
    enableWorkflowTemplateChildRoutes,
    HOME_ROUTE,
    INTEGRATIONS_ROUTE,
    LICENSES_ALL_LICENSES_ROUTE,
    LICENSES_BMS_LICENSES_ROUTE,
    LICENSES_MY_LICENSES_ROUTE,
    LICENSES_SERIAL_NUMBER_ROUTE,
    LOGIN_ROUTE,
    PASSPORT_SHARED_ROUTE,
    ROUTES,
} from "components/router/Routes";
import { SecuredRoute } from "components/router/SecuredRoute";
import { AssetCustomView } from "domain/asset-management/assets";
import {
    AUTH_AI_ASSISTANT,
    AUTH_API_KEY_VIEW_ALL,
    AUTH_EMS_CREATE_ENTITLEMENT,
    AUTH_EMS_SYNCHRONIZE,
    AUTH_EMS_VIEW,
    AUTH_LICENSE_VIEW,
    AUTH_LICENSE_VIEW_OWN,
} from "domain/authority";
import { UserDetails } from "domain/user";
import { aiAgentService, Session } from "services/ai-agent/AiAgentService";
import { apiGatewayService } from "services/api/ApiGatewayService";
import { assetViewService } from "services/asset-management/AssetCustomViewService";
import { assetService } from "services/asset-management/AssetService";
import { FLAG_ASSET_MANAGEMENT } from "services/feature/FeatureFlagService";
import * as LicenseRepository from "services/licenses/licenseRepository";
import { licenseService } from "services/licenses/LicenseService";
import { getGlobalUrl } from "services/login/endpointRepository";
import { reportViewService } from "services/report/ReportViewService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import {
    hasSubTenantCookie,
    hasTenantCookie,
    isUserParentInternal,
    tenantHasFeatureLicense,
} from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { Application, setApplication } from "store/application";
import { setViews } from "store/assetViews";
import { setInfo } from "store/bmc";
import { setHasBmsLicenses, setHasLicenseSerialKey, setTierLicenses } from "store/license";
import { clearAllTenantDetails, pushTenantDetails, TenantDetailsState } from "store/tenantDetails";
import { ThemeName } from "store/theme";
import { setTrackingSummary } from "store/trackingSummary";
import { setUser } from "store/user";
import buttons from "styles/buttons.scss";
import "styles/global.scss";
import layoutStyles from "styles/layout.scss";
import { logger } from "utils/logging";
import { initializePendo } from "utils/pendo";
import { getObject, RepositoryKey, setObject } from "utils/repository";
import { storeAndUpdateUserDetails } from "utils/session";
import { useOncePerLogin } from "utils/useOncePerLogin";

import testIds from "testIds.json";

const CURRENT_STAGE = process.env.STAGE ?? "dev";
const IS_DEV_ENV = CURRENT_STAGE === "dev" || CURRENT_STAGE === "demo";
const RELOAD_BMP = 3_600_000;
const CUSTOMER_SURVEY_VISIBILITY_END_DATE = new Date("2025-09-30T23:59:59Z");

const mapState = (state: StoreState) => ({
    authenticated: state.userReducer.user !== null,
    themeName: state.userReducer.user !== null ? state.themeReducer.themeName : ThemeName.DEFAULT,
    user: state.userReducer.user,
    hasBmsLicenses: state.licensesReducer.hasBmsLicenses,
    hasLicenseSerialKey: state.licensesReducer.hasLicenseSerialKey,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
    tierLicenses: state.licensesReducer.tierLicenses,
});
const connector = connect(mapState, {
    setUser,
    setHasBmsLicenses,
    setHasLicenseSerialKey,
    setInfo,
    setTierLicenses,
    pushTenantDetails,
    clearAllTenantDetails,
});

function setBodyClass(themeName: ThemeName, hasBmsLicenses: boolean, hasLicenseSerialKey: boolean, app: Application) {
    const classList = document.body.classList;
    for (const themeClass of Object.values(ThemeName)) {
        classList.remove(themeClass);
    }
    classList.add(themeName);

    const isDarkMode =
        themeName === ThemeName.DEFAULT_DARK ||
        themeName === ThemeName.TENANT_DARK ||
        themeName === ThemeName.SUB_TENANT_DARK;

    document.body.setAttribute("data-mode", isDarkMode ? "dark" : "light");

    if (app === Application.BAM) {
        document.body.setAttribute("data-theme", "asset-management");
    } else if (themeName === ThemeName.TENANT || themeName === ThemeName.TENANT_DARK) {
        document.body.setAttribute("data-theme", "tenant");
    } else if (themeName === ThemeName.SUB_TENANT || themeName === ThemeName.SUB_TENANT_DARK) {
        document.body.setAttribute("data-theme", "subtenant");
    } else {
        document.body.setAttribute("data-theme", "default");
    }

    if (hasSubTenantCookie()) {
        document.body.setAttribute("data-tenant-access", "subtenant");
    } else if (hasTenantCookie()) {
        document.body.setAttribute("data-tenant-access", "tenant");
    } else {
        document.body.setAttribute("data-tenant-access", "none");
    }

    enableLicenseChildRoutes(hasBmsLicenses);
    enableBmsReportChildRoutes(hasBmsLicenses);
    enableLicenseExtractionChildRoutes(hasBmsLicenses);
    enableSerialLicenseExtractionChildRoutes(hasLicenseSerialKey);
}

/**
 * This component is complex and its lifecycle can be a surprise because it's not normal: it always exists and that's
 * one reason for its complexity. This entire webapp isn't usually refreshed on browser level and as a consequence this
 * component exists during initial page load, login, all contained pages and components, logout, login again, etc.
 */
const BlanccoCommonCloud = (props: ConnectedProps<typeof connector>) => {
    // Routes component requires that the logged in user has a role so check it
    // here. This issue was introduced when user roles were added and it's only
    // an issue for those users that are logged in during BCC update in which
    // roles are introduced. Unfortunately that's probably nearly all users but
    // eventually this check won't be necessary anymore. That's when you can be
    // confident that all users have a defined role and authorities in
    // localStorage value for "user.details".
    if (!userSessionService.userHasRole()) {
        userSessionService.logout().then(() => {
            window.location.replace(LOGIN_ROUTE.path);
        });
    }

    const [isSessionChecked, setIsSessionChecked] = useState<boolean>(false);
    const [initializedFor, setInitializedFor] = useState<string | undefined>(undefined);
    const [isLanguageLoaded, setIsLanguageLoaded] = useState<boolean>(false);

    i18n.on("loaded", () => setIsLanguageLoaded(true));
    const [popupVisibility, setPopupVisibility] = React.useState(false);
    const [html, setHtml] = React.useState("");
    const [customerSurveyVisibility, setCustomerSurveyVisibility] = React.useState<boolean>(false);
    const { t } = useTranslation();
    const [sessions, setSessions] = useState<Session[]>([]);
    const application = useSelector((state: StoreState) => state.applicationReducer.selected);
    const dispatch = useDispatch();
    const availableBam = useFeature(FLAG_ASSET_MANAGEMENT);
    const userUuid = props.user?.uuid;
    const isAIPanelEnabled =
        props.authenticated &&
        tenantHasFeatureLicense("FEATURE_AI_AGENT") &&
        userSessionService.userHasAnyAuthority([AUTH_AI_ASSISTANT]);

    useOncePerLogin(initializePendo);

    const reloadOnCodeUpdate = (isInitialCheck: boolean) => {
        const localVersion = getObject(RepositoryKey.LOCAL_BMP_VERSION) ?? "";

        const url = getGlobalUrl();
        if (typeof url === "undefined") {
            return;
        }
        return apiGatewayService
            .fetch(url + "/index.html")
            .then((response) => {
                if (response.status !== 200) {
                    throw new Error("Error occurred while calling BMP.");
                }
                return response.text();
            })
            .then((html) => {
                // A unique hash is generated on each build. This hash is part of the file
                // embedded in the index.html inside the <script> tag. The content of
                // index.html is stored to local storage and then compared after each RELOAD_BMP
                // milliseconds with the newly fetched filename. If they differ, BMP gets reloaded.
                if (localVersion == "" || isInitialCheck) {
                    setObject(RepositoryKey.LOCAL_BMP_VERSION, html);
                } else {
                    if (localVersion !== html) {
                        setHtml(html);
                        setPopupVisibility(true);
                    }
                }
            })
            .catch((error) => {
                logger.error("Error occurred while checking for updates", error);
            });
    };

    function resolveTenantUuid() {
        return props.tenantDetails ? props.tenantDetails.uuid : props.user?.tenantUuid;
    }

    function shouldFetchAssetData() {
        return (
            availableBam &&
            userSessionService.userHasAllAuthorities(["AUTH_ASSET_VIEW"]) &&
            tenantHasFeatureLicense("FEATURE_ASSET_MANAGEMENT")
        );
    }

    // The loaders below fetch everything that Router and Navigation read while being built: routes, child routes and
    // route validators. They never reject, so a failed load doesn't keep the application on the loading indicator.
    function loadCustomReportViews(): Promise<void> {
        const hasFeatureLicense = props.user?.featureLicenses.includes("FEATURE_CUSTOM_REPORT_VIEWS");
        if (!hasFeatureLicense || !userSessionService.userHasAllAuthorities(["AUTH_CUSTOM_REPORT_VIEW_VIEW"])) {
            return Promise.resolve();
        }
        return reportViewService
            .refreshPaths()
            .then(() => reportViewService.fetchViews())
            .then((response) => {
                enableCustomReportViewRoutes(response.reportViews);
            })
            .catch((err) => {
                logger.error("Failed to fetch report views: ", err);
            });
    }

    function loadCustomAssetViews(): Promise<void> {
        if (!shouldFetchAssetData()) {
            return Promise.resolve();
        }
        return assetViewService
            .fetchAssetRelatedViews()
            .then((response: AssetCustomView[]) => {
                enableCustomAssetViewRoutes(response);
                dispatch(setViews(response));
            })
            .catch((err: Error) => {
                logger.error("Failed to fetch asset views: ", err);
            });
    }

    function loadBmsLicenseFlags(abortController: AbortController): Promise<void> {
        if (resolveTenantUuid() == null) {
            return Promise.resolve();
        }
        props.setHasBmsLicenses(LicenseRepository.hasAnyBmsLicenses());
        props.setHasLicenseSerialKey(LicenseRepository.hasLicenseSerialKey());

        const canViewAllLicenses = userSessionService.userHasAllAuthorities([AUTH_LICENSE_VIEW]);
        const canViewOwnLicenses = userSessionService.userHasAllAuthorities([AUTH_LICENSE_VIEW_OWN]);
        if ((!canViewAllLicenses && !canViewOwnLicenses) || !props.tenantDetails) {
            return Promise.resolve();
        }
        return licenseService
            .checkBmsLicenses(
                !canViewAllLicenses,
                abortController,
                props.tenantDetails.uuid,
                props.tenantDetails.tenantTier
            )
            .then(() => {
                props.setHasBmsLicenses(LicenseRepository.hasAnyBmsLicenses());
                props.setHasLicenseSerialKey(LicenseRepository.hasLicenseSerialKey());
            })
            .catch((err) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to check BMS licenses: ", err);
                }
            });
    }

    function loadEmsExistence(abortController: AbortController): Promise<void> {
        const tenantUuid = resolveTenantUuid();
        const region = props.tenantDetails ? props.tenantDetails.region : props.user?.region;
        if (tenantUuid == null || !shouldFetchEmsConfig()) {
            LicenseRepository.setHasEmsId(false);
            return Promise.resolve();
        }
        return licenseService
            .fetchEmsConfigurationExistence(tenantUuid, abortController, region)
            .then((exists) => {
                LicenseRepository.setHasEmsId(exists);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    LicenseRepository.setHasEmsId(false);
                }
            });
    }

    function hasSubmittedCustomerSurvey() {
        return getObject<boolean>(RepositoryKey.BMP_CUSTOMER_SURVEY);
    }

    function showBmpCustomerSurvey() {
        if (!customerSurveyVisibility) {
            return null;
        }
        return (
            <SurveyMonkeyWidget
                surveyMonkeyId={process.env.BMP_CUSTOMER_SURVEY_ID || ""}
                tenantUuid={props.tenantDetails.uuid}
                userUuid={props.user ? props.user.uuid : undefined}
            />
        );
    }

    function shouldFetchEmsConfig() {
        const tenantDetailsState = getObject<TenantDetailsState>(RepositoryKey.TENANT_DETAILS);
        const isUnderAccess = (tenantDetailsState?.stack?.length ?? 0) > 1;

        const canViewEms = userSessionService.userHasAnyAuthority([
            AUTH_EMS_VIEW,
            AUTH_EMS_CREATE_ENTITLEMENT,
            AUTH_EMS_SYNCHRONIZE,
        ]);

        return canViewEms && (isUserParentInternal() || (!isUserParentInternal() && !isUnderAccess));
    }

    useEffect(() => {
        if (!props.authenticated && !isSessionChecked && isLanguageLoaded) {
            const user = userSessionService.currentUserDetails();
            i18n.off("loaded");
            if (user != null) {
                props.setUser(user);
            }
            setIsSessionChecked(true);
        }
    }, [isLanguageLoaded, props.authenticated]);

    // The first check only stores the version of the page that is currently running. Later checks compare against it.
    useEffect(() => {
        reloadOnCodeUpdate(true);
        const reloadTimer = setInterval(() => reloadOnCodeUpdate(false), RELOAD_BMP);
        return () => clearInterval(reloadTimer);
    }, []);

    // A persisted tenant stack means that the user details were stored by an earlier page load, so refresh them once.
    useEffect(() => {
        const tenantDetailsState: TenantDetailsState | null = getObject(RepositoryKey.TENANT_DETAILS);
        if (!tenantDetailsState || tenantDetailsState.stack.length === 0) {
            return;
        }
        userSessionService.fetchUserDetails().then((user: UserDetails) => {
            storeAndUpdateUserDetails(user, {
                setUser: props.setUser,
                clearAllTenantDetails: hasTenantCookie() ? () => {} : props.clearAllTenantDetails,
                pushTenantDetails: hasTenantCookie() ? () => {} : props.pushTenantDetails,
            });
        });
    }, []);

    useEffect(() => {
        if (!props.authenticated) {
            return;
        }
        // eslint-disable-next-line no-console
        console.log("BMP Version: " + process.env.BMP_VERSION);

        if (
            !hasSubmittedCustomerSurvey() &&
            !isUserParentInternal() &&
            new Date() <= CUSTOMER_SURVEY_VISIBILITY_END_DATE
        ) {
            setCustomerSurveyVisibility(true);
        }
    }, [props.authenticated]);

    useEffect(() => {
        if (!props.authenticated || !shouldFetchAssetData()) {
            return;
        }
        const abortController = new AbortController();
        assetService
            .fetchTrackingSummary(abortController)
            .then((summary) => {
                dispatch(setTrackingSummary(summary));
            })
            .catch((err: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch tracking summary: ", err);
                }
            });
        return () => abortController.abort();
    }, [props.authenticated, props.tenantDetails?.uuid]);

    useEffect(() => {
        if (!props.authenticated || !props.tenantDetails) {
            return;
        }
        const licenseRequest = licenseService.fetchAvailableLicenses(MANAGEMENT_CONSOLE_LICENSE);
        const tierLicensesRequest = licenseService.fetchTierLicenses();
        if (shouldFetchEmsConfig()) {
            licenseService
                .fetchEmsConfiguration(props.tenantDetails.uuid)
                .then((response) => {
                    props.setInfo({
                        slEntitlement: response.slEntitlements,
                        hlEntitlement: response.hlEntitlements,
                    });
                })
                .catch(() => {
                    logger.error("Failed to fetch EMS configuration");
                });
        }

        Promise.all([licenseRequest, tierLicensesRequest]).then(([licenseResponse, tierLicensesResponse]) => {
            props.setTierLicenses(tierLicensesResponse);

            if (licenseResponse.availableLicenses > 0 && typeof licenseResponse.expiration !== "undefined") {
                const today = new Date();
                const expirationDate = new Date(licenseResponse.expiration);

                const daysLeft = today <= expirationDate;

                props.setInfo({
                    licenseExists: daysLeft,
                    availableLicenses: licenseResponse.availableLicenses,
                });
            }
        });
    }, [props.authenticated, props.tenantDetails?.uuid]);

    // Router and Navigation read routes, child routes and route validators while being built, so the loading indicator
    // stays until everything below has settled. Keyed only by user identity: repeated runs of the effect above must
    // never take the Router away from an already ready user.
    useEffect(() => {
        if (userUuid === undefined) {
            return;
        }
        let isCancelled = false;
        const abortController = new AbortController();
        if (
            application === Application.BMP &&
            availableBam &&
            props.user?.default_settings.default_view === INTEGRATIONS_ROUTE.path
        ) {
            dispatch(setApplication(Application.BAM));
        }
        Promise.all([
            loadCustomReportViews(),
            loadCustomAssetViews(),
            loadBmsLicenseFlags(abortController),
            loadEmsExistence(abortController),
        ]).then(() => {
            if (!isCancelled) {
                setInitializedFor(userUuid);
            }
        });
        return () => {
            isCancelled = true;
            abortController.abort();
        };
    }, [userUuid]);

    useEffect(() => {
        if (!isAIPanelEnabled) {
            setSessions([]);
            return;
        }
        aiAgentService
            .fetchSessions()
            .then(setSessions)
            .catch(() => {
                // Keep empty sessions on failure.
            });
    }, [isAIPanelEnabled]);

    setBodyClass(props.themeName, props.hasBmsLicenses, props.hasLicenseSerialKey, application);
    function isOnBam() {
        return availableBam && application === Application.BAM;
    }

    const appLifecycleState = resolveAppLifecycle({
        isSessionChecked,
        isAuthenticated: props.authenticated,
        identityKey: userUuid,
        initializedFor,
    });
    const isPublicPassportPage = window.location.pathname === PASSPORT_SHARED_ROUTE.path;
    const isLoaderVisible = appLifecycleState !== AppLifecycle.LOGGED_OUT && appLifecycleState !== AppLifecycle.READY;

    return (
        <>
            <div className={style.applicationContainer}>
                {isLoaderVisible ? (
                    <LoadingIndicator />
                ) : (
                    <Router>
                        <AIPanelProvider isEnabled={isAIPanelEnabled} sessions={sessions} setSessions={setSessions}>
                            <LayoutProvider>
                                <Switch>
                                    {ROUTES.map((route, index) => {
                                        if (route.childRoutes.includes(ALL_WORKFLOWS_ROUTE)) {
                                            enableWorkflowTemplateChildRoutes(props.user?.tenantType === "INTERNAL");
                                        }
                                        if (
                                            route.childRoutes.includes(LICENSES_ALL_LICENSES_ROUTE) ||
                                            route.path.includes(LICENSES_BMS_LICENSES_ROUTE.path) ||
                                            route.path.includes(ALL_BMS_REPORTS_ROUTE.path) ||
                                            route.path.includes(LICENSES_MY_LICENSES_ROUTE.path) ||
                                            route.path.includes(LICENSES_SERIAL_NUMBER_ROUTE.path)
                                        ) {
                                            if (props.hasBmsLicenses) {
                                                enableLicenseChildRoutes(props.hasBmsLicenses);
                                                enableLicenseExtractionChildRoutes(props.hasBmsLicenses);
                                                enableBmsReportChildRoutes(props.hasBmsLicenses);
                                            }

                                            if (props.hasLicenseSerialKey) {
                                                enableSerialLicenseExtractionChildRoutes(props.hasLicenseSerialKey);
                                            }
                                        }
                                        if (route.path.includes(API_KEYS_ROUTE.path)) {
                                            enableOrDisableAllApiKeys(
                                                props.user?.role.authorities.includes(AUTH_API_KEY_VIEW_ALL)
                                            );
                                        }
                                        if (!route.isValidUser(props.user)) {
                                            return null;
                                        }
                                        if (!route.isAccessibleTo(props.tenantDetails)) {
                                            return null;
                                        }
                                        return (
                                            <SecuredRoute
                                                path={route.path}
                                                key={index}
                                                component={route.component}
                                                secured={route.secured}
                                                authenticated={props.authenticated}
                                            />
                                        );
                                    })}
                                    <Route
                                        render={({ location }) => {
                                            if (!props.authenticated) {
                                                setObject(
                                                    RepositoryKey.LOGIN_RETURN_TO,
                                                    location.pathname + location.search
                                                );
                                                return <Redirect to={LOGIN_ROUTE.path} />;
                                            }
                                            return (
                                                <Redirect
                                                    to={
                                                        hasTenantCookie()
                                                            ? props.user?.default_settings &&
                                                              props.user?.default_settings.tenant_access_default_view
                                                                ? props.user?.default_settings
                                                                      .tenant_access_default_view
                                                                : DELIVERYHISTORY_LICENSES_ROUTE.path
                                                            : isOnBam()
                                                              ? (props.user?.default_settings?.default_view ??
                                                                INTEGRATIONS_ROUTE.path)
                                                              : (props.user?.default_settings?.default_view ??
                                                                HOME_ROUTE.path)
                                                    }
                                                />
                                            );
                                        }}
                                    />
                                </Switch>
                                {isAIPanelEnabled && !isPublicPassportPage && <AIPanel layout="floating" />}
                                {IS_DEV_ENV && <DevTools />}
                            </LayoutProvider>
                        </AIPanelProvider>
                    </Router>
                )}
            </div>
            <Modal
                isOpen={popupVisibility}
                hideModal={() => setPopupVisibility(false)}
                modalTitle={t("UpdatePopup.title")}
            >
                {t("UpdatePopup.content")}

                <div className={layoutStyles.buttonContainer}>
                    <button
                        type={"submit"}
                        onClick={() => setPopupVisibility(false)}
                        data-testid={testIds.common.confirmationDialog.undoButton}
                        className={buttons.secondaryButtonWithoutIcon}
                    >
                        {t("UpdatePopup.skipButton")}
                    </button>
                    <button
                        type={"submit"}
                        onClick={() => {
                            setObject(RepositoryKey.LOCAL_BMP_VERSION, html);
                            window.location.reload();
                        }}
                        data-testid={testIds.common.confirmationDialog.confirmButton}
                        className={buttons.primaryButtonWithoutIcon}
                    >
                        {t("UpdatePopup.refreshButton")}
                    </button>
                </div>
            </Modal>
            <Modal
                isOpen={customerSurveyVisibility}
                width="920px"
                hideModal={() => {
                    setCustomerSurveyVisibility(false);
                    setObject(RepositoryKey.BMP_CUSTOMER_SURVEY, true);
                    usageStatisticsService.sendEvent({
                        category: Category.SURVEY,
                        action: Action.CLOSE_CUSTOMER_SURVEY,
                    });
                }}
            >
                {showBmpCustomerSurvey()}
            </Modal>
        </>
    );
};

export default connector(BlanccoCommonCloud);
